import * as Immutable from "immutable"

import actionTypes, { loadCustomRegionsTypes } from "subfeatures/RegionSelectionPage/action-types"
import * as djangioActionCreators from "shared/djangio/action-creators"
import * as globalActionCreators from "global-action-creators"
import { sendUserAction } from "shared/user-actions/user-actions-action-creators"

import { isRegionCustom } from "shared/region-selection/helperFunctions"
import { isFeatureEnabled } from "shared/featureflags/helperFunctions"

import constants from "subfeatures/RegionSelectionPage/constants"
import urls from "shared/imports/sharedPaths"
import { downloadCenterPaths } from "imports/desktopPaths"

// RegionSelectionPage

export const loadCustomRegions = () =>
    djangioActionCreators.resourceGetList(
        DjangIO.app.userdata.models.CustomRegionSelection,
        loadCustomRegionsTypes,
        { limit: 0 },
        {},
        true,
    )

export const deleteCustomRegion = (props) => (dispatch) =>
    swal({
        closeOnClickOutside: false,
        closeOnEsc: false,
        icon: "warning",
        title: "Are you sure?",
        // text: `${labels.text} ${isEdit ? 'edited' : 'submitted'} successfully.`,
        buttons: {
            cancel: "No",
            // if user just edited an object, then ask about creating 'a new' object, o.w. word is 'another'
            continue: `Yes, delete this region`,
        },
    }).then((value) => {
        if (value === "continue") {
            DjangIO.app.userdata.models.CustomRegionSelection.objects
                .get(props.params.datumId)
                .update({ archived: true })
                .PATCH()
                .then(() => {
                    dispatch(globalActionCreators.popPage())
                    // set the redux-form values to the initialValues
                    props.reset()
                })
                .catch((error) => {
                    swal("Uh oh!", error.response.data.error_message, "error")
                })
        }
    })

export const updateField = (fieldName, fieldValue) => (dispatch) =>
    dispatch({
        type: actionTypes.UPDATE_FIELD,
        fieldName,
        fieldValue,
    })

/**
 * This function gets the previous page's url from the store.
 * The 'change_region' logic in app/views.py uses the current_url to decide where to redirect the user. Since,
 * we popup a whole page with the RegionSelectionPage, we need to pass the previous url so that current_url
 * is not always /switch_region/.
 * @param  {object} state [current app state]
 * @return {string}       [encoded url]
 */
export const getOverrideUrl = ({ previousUrl, removeItemId = false }) => {
    if (!previousUrl || previousUrl.includes("undefined")) {
        // if you refresh the page and then try to get previousRoute, it is
        // the string "undefinedundefined." So, default to /home/ in this case
        previousUrl = urls.home
    }
    // if we should remove the datum id...
    if (removeItemId) {
        // DLC Case
        if (
            previousUrl.includes(downloadCenterPaths.profileDownloadCenterPrefix) &&
            // don't replace the datum for blank downloads
            !previousUrl.includes(
                `${downloadCenterPaths.profileDownloadCenterPrefix}` +
                    `${DjangIO.app.models.QuorumDataType.blank_download.value}/`,
            )
        ) {
            // get the parts of the url that match `someNumber/` (eg 123/)
            const numberMatches = previousUrl.match(/[0-9]+\//g)
            if (numberMatches) {
                // get the last instance of this, which should be the datum id
                //
                // TODO: by the second redirect, this code will have stripped out the
                // qdt because of the way the `nextUrl` is being set in
                // downloadCenterReducerWithUrlMiddleware in download-center/reducer.
                // We need to figure out either
                // 1) when to strip this out based on the number of previous redirects
                // 2) change the downloadCenterReducerWithUrlMiddleware logic so that
                // the `nextUrl` is properly pushed onto the routing reducer (instead of the
                // history just being changed) for when we access state.routing.get("previousRoute")
                const dataItemId = numberMatches[numberMatches.length - 1]
                // remove the last instance of the number from the url
                // since it could be the same as the qdt
                // (eg geoshape = 97 & some alabama district is 97)
                previousUrl = previousUrl.substring(0, previousUrl.lastIndexOf(dataItemId))
            }
        }
    }
    return encodeURIComponent(previousUrl)
}

/**
 * Changes the user's current region, through the /change_region/ endpoint.
 * @param  {number} newRegionValue [SupportedRegion enum value]
 * @param  {string} previousUrl    URL of the previous page to be redirected to after changing region
 */
export const changeRegion = (newRegionValue, previousUrl) => (dispatch, getState) => {
    const regionEnumItem = DjangIO.app.models.SupportedRegion.by_value(newRegionValue)
    const newRegionAbbrev =
        isRegionCustom(newRegionValue) || !regionEnumItem
            ? // either a custom region value or a region that is not yet supported
              newRegionValue
            : // regular region enum value
              regionEnumItem.abbrev
    dispatch(sendUserAction(DjangIO.app.useractions.types.UserActionType.change_region, newRegionAbbrev))

    // change_region logic in app/views.py uses the current_url to decide where to
    // redirect the user, so pass previous url (not a good way to pop the current
    // page before the location.replace for change_region, user would see 2 major segues
    // which is disorienting)
    const overrideUrl = getOverrideUrl({ previousUrl, removeItemId: true })

    // prepare page for redirect - will get a fresh slate
    localStorage.clear()
    sessionStorage.clear()

    // go to /change_region/<region-abbrev>/ to trigger the change_region logic
    // (window.location.replace triggers a page refresh, change_region redirects to proper place)
    window.location.replace(`/change_region/${newRegionAbbrev}/?force_mobile=${false}&override_url=${overrideUrl}`)
}

// CustomRegionForm

export const createNewCustomRegion = (immutableValues, dispatch, props) => {
    const values = immutableValues.toJS()
    const updateDict = {
        name: values.name,
        // submit list of region.abbrev
        regions: values.regions.map((value) => DjangIO.app.models.SupportedRegion.by_value(value).abbrev),
    }
    if (props.params.datumId) {
        // make patch request
        return DjangIO.app.userdata.models.CustomRegionSelection.objects
            .get(props.params.datumId)
            .update(updateDict)
            .PATCH()
    } else {
        // create custom region selection object
        return DjangIO.app.userdata.models.CustomRegionSelection.objects
            .create({
                ...updateDict,
                user_info_id: Userdata.user_info_id,
            })
            .POST()
    }
}

export const customRegionValidation = (immutableValues) => {
    const { name, regions } = immutableValues.toJS()
    const errors = {}

    // must have a name
    if (!name || !name.length) {
        errors.name = constants.noNameError
    }
    // must have at least one region
    if (!regions || !regions.length) {
        errors.regions = constants.noRegionsError
    }

    return errors
}

export const customRegionWarning = () => void 0
